package com.fantasytenis.web.screens.skills

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.*
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.components.skills.*
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.borderBottom
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Skill Ratings Hub Screen
 *
 * Main entry point for exploring player skills. Shows:
 * - Search bar
 * - Hidden gems carousel
 * - Category leaderboards preview
 * - Weekly movers (trending players)
 */
@Composable
fun SkillRatingsHubScreen() {
    val colors = Theme.colors
    val scope = rememberCoroutineScope()

    // Data states
    var hiddenGems by remember { mutableStateOf<List<HiddenGemPlayer>>(emptyList()) }
    var categoryLeaderboards by remember { mutableStateOf<Map<Category, List<SkillLeaderboardEntry>>>(emptyMap()) }
    var trendingUp by remember { mutableStateOf<List<TrendingPlayer>>(emptyList()) }
    var trendingDown by remember { mutableStateOf<List<TrendingPlayer>>(emptyList()) }

    // Loading states
    var isLoadingGems by remember { mutableStateOf(true) }
    var isLoadingLeaderboards by remember { mutableStateOf(true) }
    var isLoadingTrending by remember { mutableStateOf(true) }

    // Error state
    var error by remember { mutableStateOf<String?>(null) }

    // Load data on mount
    LaunchedEffect(Unit) {
        // Load hidden gems
        scope.launch {
            DependencyContainer.getHiddenGemsUseCase(limit = 6).fold(
                onSuccess = { gems -> hiddenGems = gems },
                onFailure = { /* Non-critical */ }
            )
            isLoadingGems = false
        }

        // Load category leaderboards (top 3 for each)
        scope.launch {
            val leaderboards = mutableMapOf<Category, List<SkillLeaderboardEntry>>()
            Category.entries.forEach { category ->
                DependencyContainer.getSkillLeaderboardUseCase(category, limit = 3).fold(
                    onSuccess = { (entries, _) -> leaderboards[category] = entries },
                    onFailure = { /* Non-critical */ }
                )
            }
            categoryLeaderboards = leaderboards
            isLoadingLeaderboards = false
        }

        // Load trending players
        scope.launch {
            DependencyContainer.getTrendingPlayersUseCase(TrendDirection.UP, limit = 5).fold(
                onSuccess = { players -> trendingUp = players },
                onFailure = { /* Non-critical */ }
            )
            DependencyContainer.getTrendingPlayersUseCase(TrendDirection.DOWN, limit = 5).fold(
                onSuccess = { players -> trendingDown = players },
                onFailure = { /* Non-critical */ }
            )
            isLoadingTrending = false
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = "Skill Ratings",
            showBackButton = true,
            onBackClick = { NavigationState.goBack() }
        )

        // Content
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(24.px)
            }
        }) {
            // Hidden Gems Section
            Section({
                style {
                    marginBottom(32.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(16.px)
                    }
                }) {
                    H3({
                        style {
                            fontSize(18.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            margin(0.px)
                        }
                    }) {
                        Text("Hidden Gems")
                    }
                }

                if (isLoadingGems) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(16.px)
                            overflow("auto")
                        }
                    }) {
                        repeat(3) {
                            Skeleton(width = 200.px, height = 160.px)
                        }
                    }
                } else if (hiddenGems.isEmpty()) {
                    Div({
                        style {
                            color(colors.textSecondary)
                            fontSize(14.px)
                        }
                    }) {
                        Text("No hidden gems found")
                    }
                } else {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(16.px)
                            overflow("auto")
                            paddingBottom(8.px)
                        }
                    }) {
                        hiddenGems.forEach { gem ->
                            Div({
                                style {
                                    flexShrink(0)
                                    width(200.px)
                                }
                            }) {
                                HiddenGemCard(
                                    player = gem,
                                    onClick = { NavigationState.navigate(Route.PlayerSkillDetail(gem.playerId)) }
                                )
                            }
                        }
                    }
                }
            }

            // Category Leaderboards Section
            Section({
                style {
                    marginBottom(32.px)
                }
            }) {
                H3({
                    style {
                        fontSize(18.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                        marginBottom(16.px)
                    }
                }) {
                    Text("Leaderboards by Skill")
                }

                if (isLoadingLeaderboards) {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                            gap(16.px)
                        }
                    }) {
                        repeat(5) {
                            Skeleton(width = 100.percent, height = 200.px)
                        }
                    }
                } else {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                            gap(16.px)
                        }
                    }) {
                        Category.entries.forEach { category ->
                            CategoryLeaderboardCard(
                                category = category,
                                entries = categoryLeaderboards[category] ?: emptyList(),
                                onViewAll = { NavigationState.navigate(Route.SkillLeaderboard(category.name.lowercase())) }
                            )
                        }
                    }
                }
            }

            // Weekly Movers Section
            if (!isLoadingTrending && (trendingUp.isNotEmpty() || trendingDown.isNotEmpty())) {
                Section({
                    style {
                        marginBottom(32.px)
                    }
                }) {
                    H3({
                        style {
                            fontSize(18.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            marginBottom(16.px)
                        }
                    }) {
                        Text("Weekly Movers")
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                            gap(16.px)
                        }
                    }) {
                        // Rising
                        if (trendingUp.isNotEmpty()) {
                            TrendingSection(
                                title = "Rising",
                                players = trendingUp,
                                onPlayerClick = { player -> NavigationState.navigate(Route.PlayerSkillDetail(player.playerId)) }
                            )
                        }

                        // Falling
                        if (trendingDown.isNotEmpty()) {
                            TrendingSection(
                                title = "Falling",
                                players = trendingDown,
                                onPlayerClick = { player -> NavigationState.navigate(Route.PlayerSkillDetail(player.playerId)) }
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * Category leaderboard preview card
 */
@Composable
private fun CategoryLeaderboardCard(
    category: Category,
    entries: List<SkillLeaderboardEntry>,
    onViewAll: () -> Unit
) {
    val colors = Theme.colors
    val categoryColor = getCategoryColor(category)

    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            overflow("hidden")
            border(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        // Header with category color
        Div({
            style {
                backgroundColor(categoryColor)
                color(Colors.White)
                padding(12.px, 16.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontWeight(600)
                    fontSize(16.px)
                }
            }) {
                Text(category.displayName)
            }
        }

        // Top 3 entries
        Div({
            style {
                padding(12.px)
            }
        }) {
            if (entries.isEmpty()) {
                Div({
                    style {
                        color(colors.textSecondary)
                        fontSize(14.px)
                        textAlign("center")
                        padding(16.px)
                    }
                }) {
                    Text("No data available")
                }
            } else {
                entries.forEachIndexed { index, entry ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            padding(8.px, 0.px)
                            if (index < entries.size - 1) {
                                borderBottom(1.px, LineStyle.Solid, colors.border)
                            }
                        }
                    }) {
                        // Rank
                        Span({
                            style {
                                width(24.px)
                                fontWeight(700)
                                color(
                                    when (entry.skillRank) {
                                        1 -> Colors.Medal.GoldBackground
                                        2 -> Colors.Medal.SilverBackground
                                        3 -> Colors.Medal.BronzeBackground
                                        else -> colors.textSecondary
                                    }
                                )
                            }
                        }) {
                            Text("${entry.skillRank}")
                        }

                        // Name
                        Span({
                            style {
                                flex(1)
                                fontSize(14.px)
                                color(colors.textPrimary)
                            }
                        }) {
                            Text(entry.playerName)
                        }

                        // Cost
                        Span({
                            style {
                                fontWeight(700)
                                fontSize(16.px)
                                color(categoryColor)
                            }
                        }) {
                            Text("${entry.skillCost}")
                        }
                    }
                }
            }

            // View All button
            Div({
                style {
                    marginTop(12.px)
                    textAlign("center")
                }
            }) {
                Span({
                    style {
                        color(colors.primary)
                        fontSize(14.px)
                        fontWeight(600)
                        cursor("pointer")
                    }
                    onClick { onViewAll() }
                }) {
                    Text("View All \u2192")
                }
            }
        }
    }
}

/**
 * Trending players section
 */
@Composable
private fun TrendingSection(
    title: String,
    players: List<TrendingPlayer>,
    onPlayerClick: (TrendingPlayer) -> Unit
) {
    val colors = Theme.colors
    val isRising = title == "Rising"

    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            border(1.px, LineStyle.Solid, colors.border)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            Span({
                style {
                    fontSize(16.px)
                }
            }) {
                Text(if (isRising) "\u2191" else "\u2193")
            }
            Span({
                style {
                    fontWeight(600)
                    fontSize(16.px)
                    color(if (isRising) Colors.Success else Colors.Error)
                }
            }) {
                Text(title)
            }
        }

        players.forEach { player ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    padding(8.px, 0.px)
                    borderBottom(1.px, LineStyle.Solid, colors.border)
                    cursor("pointer")
                }
                onClick { onPlayerClick(player) }
            }) {
                Div({
                    style {
                        flex(1)
                    }
                }) {
                    Div({
                        style {
                            fontSize(14.px)
                            fontWeight(500)
                            color(colors.textPrimary)
                        }
                    }) {
                        Text(player.playerName)
                    }
                    Div({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(player.category.displayName)
                    }
                }

                TrendBadge(player.trend)
            }
        }
    }
}
